/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Plane, CheckCircle2, ChevronRight, Award, Clock, Wifi, Shield, ArrowRight,
  BookOpen, Compass, Cloud, Radio, Wind, Users, Cpu, AlertTriangle, Sparkles,
} from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import SEO from "../components/SEO";
import { paymentsApi } from "./portal/shell";

// Drone-in-flight hero (aerial view over lake) and a curriculum-section
// backdrop (pilot in safety vest operating a drone).
const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/nqvumnjg_IMG_5588-3.png";
const CURR_IMG =
  "https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/mardh6vj_IMG_5660-3.png";

const API = process.env.REACT_APP_BACKEND_URL;

// Advanced RPAS knowledge areas — Transport Canada Small Advanced Exam.
// Aligned with CARs Part IX + AC 903-001 / TP 15263 subject matter guide.
const MODULES = [
  { code: "01", title: "Air Law, Air Traffic Rules & Procedures", hours: "6 h", icon: Shield },
  { code: "02", title: "RPAS Airframes, Power Plants, Propulsion & Systems", hours: "4 h", icon: Cpu },
  { code: "03", title: "Human Factors", hours: "3 h", icon: Users },
  { code: "04", title: "Meteorology", hours: "5 h", icon: Cloud },
  { code: "05", title: "Navigation", hours: "4 h", icon: Compass },
  { code: "06", title: "Flight Operations", hours: "4 h", icon: Wind },
  { code: "07", title: "Theory of Flight", hours: "3 h", icon: Plane },
  { code: "08", title: "Radiotelephony", hours: "2 h", icon: Radio },
];

const BENEFITS = [
  { icon: Award, t: "Full TC Syllabus", d: "Every knowledge area on the Transport Canada Small Advanced Exam — 8 subjects, ~30 study hours." },
  { icon: Clock, t: "Self-Paced", d: "Study when it works for you. Resume from any device, any time." },
  { icon: Wifi, t: "100% Online", d: "No commutes, no classrooms. All materials stream to any modern browser." },
  { icon: Shield, t: "Practice Exams", d: "Randomized full-length mock exams with instant scoring and answer rationale." },
];

const FAQ = [
  {
    q: "What certification does this prepare me for?",
    a: "This course prepares you for the Transport Canada Small Advanced Exam — the written knowledge test required for the Advanced Operations RPAS Pilot Certificate under CARs Part IX. It covers every subject Transport Canada tests in the Advanced knowledge requirements.",
  },
  {
    q: "Is this course itself Transport Canada certified?",
    a: "Our curriculum is built to the Transport Canada knowledge requirements for Advanced Operations (CARs Part IX; AC 903-001; TP 15263). Completing this course is not a substitute for the Small Advanced Exam or the Flight Review — both are conducted separately by Transport Canada and a Transport Canada–approved Flight Reviewer.",
  },
  {
    q: "What else do I need beyond this course?",
    a: "Two things: (1) pass the Small Advanced Online Exam on the Transport Canada Drone Management Portal (80% pass mark), and (2) pass an in-person Flight Review with an approved Flight Reviewer. Once both are complete, Transport Canada issues your Advanced Pilot Certificate.",
  },
  {
    q: "How long do I have access?",
    a: "Lifetime access. Once you're enrolled you can revisit every lesson, quiz, and practice exam whenever you need to refresh — including before renewals or recurrent training.",
  },
  {
    q: "What's the passing standard?",
    a: "Module quizzes require 70% to advance. Full-length practice exams require 80%, matching the Transport Canada Small Advanced Exam pass mark. Unlimited quiz retakes; practice exams have a 90-minute cooldown between attempts (the real TC exam requires 24 hours — our practice tool is deliberately looser for study flow).",
  },
  {
    q: "How is the course delivered?",
    a: "Written lessons with illustrations, downloadable PDF study guides, interactive quizzes with answer explanations, and full-length practice exams. Optional video walkthroughs for select modules are planned.",
  },
];

export default function GroundSchool() {
  return (
    <>
      <SEO
        title="Advanced RPAS Ground School | Transport Canada Drone Pilot Course | Thunderbird Geospatial"
        description="Complete online Advanced RPAS drone pilot ground school for the Transport Canada Small Advanced Exam. 8 modules, practice exams, certificate on completion. Self-paced, lifetime access."
        canonical="https://tbirdgeo.com/groundschool/"
      />
      <Toaster theme="dark" position="top-right" />
      <main className="min-h-screen bg-tb-bg text-tb-text">
        <Navbar />
        <Hero />
        <Overview />
        <Benefits />
        <Curriculum />
        <ExamFormat />
        <Pricing />
        <ComplianceDisclaimer />
        <FAQSection />
        <Waitlist />
        <Footer />
      </main>
    </>
  );
}

/* ---------------------------- HERO ---------------------------- */
function Hero() {
  return (
    <section className="relative pt-24 pb-20 overflow-hidden" data-testid="gs-hero">
      {/* Aviation backdrop */}
      <motion.div
        initial={{ scale: 1.06, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.4, ease: [0.2, 0.8, 0.2, 1] }}
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${HERO_IMG}')`,
          filter: "saturate(0.9) contrast(1.05)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black" />
      <div className="absolute inset-0 tb-grid-overlay mix-blend-overlay opacity-40" />

      {/* HUD instrument-panel decorative frame */}
      <div className="absolute top-24 right-8 hidden md:block pointer-events-none opacity-70">
        <div className="border border-tb-teal/40 rounded-full w-32 h-32 flex items-center justify-center">
          <div className="border border-tb-teal/60 rounded-full w-24 h-24 flex items-center justify-center relative">
            <Plane size={22} className="text-tb-teal rotate-45" />
            <div className="absolute inset-0 rounded-full border-t border-tb-teal/80" style={{ transform: "rotate(30deg)" }} />
          </div>
        </div>
        <div className="text-center mt-2 font-mono text-[9px] tracking-[0.2em] text-tb-teal/70">
          HDG · 049°
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-8 h-px bg-tb-teal" />
          <span className="font-mono text-[10px] tracking-[0.3em] text-tb-teal uppercase">
            Advanced RPAS · Transport Canada Certified Preparation
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold leading-[1.02] tracking-tight max-w-4xl"
        >
          Advanced RPAS ground school.<br />
          <span className="text-tb-teal">Cleared for takeoff.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-6 max-w-2xl text-base sm:text-lg text-tb-text/80 leading-relaxed"
        >
          A complete online course for the Transport Canada
          <span className="text-tb-teal"> Small Advanced Exam</span> — the written
          knowledge test required for the Advanced Operations RPAS Pilot Certificate.
          Eight modules, hundreds of practice questions, full-length mock exams,
          and a completion certificate — study on your schedule, from any device.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          <a
            href="#pricing"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-tb-teal text-black font-semibold tracking-tight hover:brightness-110 transition"
            data-testid="gs-hero-cta"
          >
            Enrol — $249 CAD <ArrowRight size={16} />
          </a>
          <a
            href="#curriculum"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white/90 font-medium hover:border-tb-teal/60 hover:text-white transition"
          >
            View the curriculum
          </a>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 font-mono text-[11px] tracking-[0.15em] text-tb-text/60 hover:text-tb-teal uppercase transition"
            data-testid="gs-hero-login"
          >
            Student sign-in →
          </Link>
        </motion.div>

        <div className="mt-14 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[10px] tracking-[0.2em] text-tb-text/60 uppercase">
          <div><span className="text-tb-teal">◆</span> 8 Modules</div>
          <div><span className="text-tb-teal">◆</span> ~30 Study Hours</div>
          <div><span className="text-tb-teal">◆</span> Practice Exams</div>
          <div><span className="text-tb-teal">◆</span> Certificate on Completion</div>
          <div><span className="text-tb-teal">◆</span> $249 CAD · Lifetime Access</div>
        </div>
      </div>

      {/* Instrument-tick strip below hero */}
      <div className="mt-14 relative border-t border-b border-tb-teal/20 py-3 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-full flex items-center gap-8 font-mono text-[10px] tracking-[0.4em] text-tb-teal/50 whitespace-nowrap animate-scroll-x">
          <span>CARs · PART IX</span><span>·</span><span>ADVANCED OPS</span><span>·</span>
          <span>SMALL RPA · 250 g – 25 kg</span><span>·</span><span>VLOS</span><span>·</span>
          <span>MTOW</span><span>·</span><span>NAV CANADA · NAV DRONE</span><span>·</span>
          <span>METAR / TAF</span><span>·</span><span>CONTROLLED AIRSPACE</span><span>·</span>
          <span>SITE SURVEY</span><span>·</span><span>RTH</span><span>·</span>
          <span>GEO-FENCING</span><span>·</span><span>RADIOTELEPHONY</span><span>·</span>
          <span>CARs · PART IX</span><span>·</span><span>ADVANCED OPS</span><span>·</span>
          <span>SMALL RPA · 250 g – 25 kg</span><span>·</span><span>VLOS</span><span>·</span>
          <span>MTOW</span><span>·</span><span>NAV CANADA · NAV DRONE</span><span>·</span>
          <span>METAR / TAF</span><span>·</span><span>CONTROLLED AIRSPACE</span><span>·</span>
          <span>SITE SURVEY</span><span>·</span><span>RTH</span><span>·</span>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- OVERVIEW ---------------------------- */
function Overview() {
  return (
    <section className="py-20 border-b border-white/5" data-testid="gs-overview">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="font-mono text-[10px] tracking-[0.3em] text-tb-teal uppercase mb-4">
            Section 01 · Overview
          </div>
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight leading-tight">
            Built to the standard.<br />Designed for real students.
          </h2>
        </div>
        <div className="md:col-span-3 text-tb-text/80 text-base leading-relaxed space-y-4">
          <p>
            The Advanced Operations RPAS Pilot Certificate lets you fly commercially
            in controlled airspace, near people, and near aerodromes — the flight
            profiles most real-world surveying, mapping, film, and inspection work
            requires. To earn it you must pass the Transport Canada Small Advanced
            Exam and a Flight Review with an approved Flight Reviewer.
          </p>
          <p>
            Our course walks you through every knowledge area on the Small
            Advanced Exam — from CARs Part IX and airspace to weather, navigation,
            RPAS systems, human factors, and radiotelephony — with clear lessons,
            illustrations, and continuous knowledge checks.
          </p>
          <p>
            You'll build confidence with module quizzes (70% to advance) and prove
            readiness with full-length practice exams (80% to pass) modelled on
            the Transport Canada Small Advanced Exam. Every wrong answer comes
            with a plain-language explanation.
          </p>
          <p className="text-tb-text/60 text-sm border-l-2 border-tb-teal/40 pl-4">
            This is a knowledge-only course. The Small Advanced Exam and Flight
            Review are conducted separately through Transport Canada and an
            approved Flight Reviewer.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- BENEFITS ---------------------------- */
function Benefits() {
  return (
    <section className="py-20 border-b border-white/5" data-testid="gs-benefits">
      <div className="max-w-6xl mx-auto px-6">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tb-teal uppercase mb-4">
          Section 02 · Why This Course
        </div>
        <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mb-12">
          Ground school that respects your time.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="p-6 rounded-xl border border-white/10 bg-white/[0.02] hover:border-tb-teal/40 hover:bg-white/[0.04] transition-all"
            >
              <b.icon size={22} className="text-tb-teal mb-3" />
              <div className="font-display font-bold text-lg mb-1">{b.t}</div>
              <div className="text-tb-text/70 text-sm leading-relaxed">{b.d}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- CURRICULUM ---------------------------- */
function Curriculum() {
  return (
    <section id="curriculum" className="py-20 relative border-b border-white/5" data-testid="gs-curriculum">
      <div
        aria-hidden
        className="absolute inset-0 opacity-60 bg-cover bg-center"
        style={{
          backgroundImage: `url('${CURR_IMG}')`,
          filter: "saturate(0.95) contrast(1.05)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-tb-bg/85 via-tb-bg/55 to-tb-bg/85" />

      <div className="relative max-w-6xl mx-auto px-6">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tb-teal uppercase mb-4">
          Section 03 · Curriculum · Small Advanced Exam
        </div>
        <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mb-3">
          Eight modules. Every subject Transport Canada tests.
        </h2>
        <p className="text-tb-text/70 max-w-2xl mb-10">
          Progress through the modules in order, or jump to any completed module for
          review. Every module ends with a knowledge check and links to the
          relevant CARs Part IX / AC 903-001 references.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {MODULES.map((m, i) => (
            <motion.div
              key={m.code}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.03 }}
              className="group p-4 rounded-lg border border-white/10 bg-black/40 hover:border-tb-teal/50 transition-all flex items-start gap-3"
              data-testid={`gs-module-${m.code}`}
            >
              <div className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-md bg-tb-teal/10 border border-tb-teal/30 flex items-center justify-center">
                <m.icon size={16} className="text-tb-teal" />
              </div>
              <div className="min-w-0">
                <div className="font-mono text-[10px] tracking-[0.2em] text-tb-text/50">
                  MOD · {m.code}
                </div>
                <div className="font-display font-semibold text-sm leading-snug mt-0.5 group-hover:text-tb-teal transition">
                  {m.title}
                </div>
                <div className="font-mono text-[10px] tracking-[0.15em] text-tb-text/50 mt-1">
                  {m.hours}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- PRICING ---------------------------- */
function Pricing() {
  const [busy, setBusy] = useState(false);

  const enroll = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await paymentsApi.checkout("advanced-rpas-2026");
      window.location.href = r.url;
    } catch (ex) {
      toast.error(ex.message || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <section id="pricing" className="py-20 border-b border-white/5" data-testid="gs-pricing">
      <div className="max-w-6xl mx-auto px-6">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tb-teal uppercase mb-4">
          Section 04 · Pricing
        </div>
        <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mb-12 max-w-2xl">
          One course. One price. Everything included.
        </h2>

        <div className="relative rounded-2xl border border-tb-teal/40 bg-gradient-to-br from-black to-tb-bg overflow-hidden p-8 sm:p-10 max-w-2xl">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-tb-teal/10 blur-3xl pointer-events-none" />
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-tb-teal uppercase mb-2">
                Advanced RPAS Ground School
              </div>
              <h3 className="text-2xl sm:text-3xl font-display font-bold">
                Small Advanced Exam Prep · Complete Package
              </h3>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-5xl font-display font-extrabold">$249</span>
                <span className="text-tb-text/60 font-mono text-sm tracking-widest">CAD</span>
              </div>
              <div className="mt-1 text-xs text-tb-text/50 font-mono tracking-wider">
                one-time · applicable taxes calculated at checkout
              </div>
            </div>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] tracking-[0.2em] font-mono uppercase border border-tb-teal/50 text-tb-teal"
              data-testid="gs-pricing-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-tb-teal animate-pulse" />
              Enrolment Open
            </span>
          </div>

          <ul className="mt-8 grid sm:grid-cols-2 gap-2.5 text-sm">
            {[
              "8 modules · ~30 study hours",
              "Practice quizzes with answer rationale",
              "50-question / 60-min practice exams",
              "Progress tracking on any device",
              "Downloadable PDF study guides",
              "Certificate of Completion (PDF)",
              "QR-verified certificate serial",
              "Lifetime access · no renewals",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2 text-tb-text/85">
                <CheckCircle2 size={16} className="text-tb-teal mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 rounded-lg border border-tb-teal/25 bg-tb-teal/[0.04] px-4 py-3 flex items-start gap-3" data-testid="gs-pricing-bonus">
            <Sparkles size={16} className="text-tb-teal mt-0.5 flex-shrink-0" />
            <div className="text-sm text-tb-text/85 leading-relaxed">
              <span className="font-mono text-[10px] tracking-[0.2em] text-tb-teal uppercase mr-2">Includes bonus</span>
              <span className="text-white">Flight Review Prep</span> — a supplementary module to help you get ready for the practical evaluation with a Transport Canada–approved Flight Reviewer. Not part of the graded curriculum.
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={enroll}
              disabled={busy}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-tb-teal text-black font-semibold hover:brightness-110 transition disabled:opacity-60"
              data-testid="gs-pricing-cta"
            >
              {busy ? "Loading checkout…" : "Enrol now"} <ChevronRight size={16} />
            </button>
            <span className="inline-flex items-center px-4 py-3 font-mono text-[11px] tracking-[0.15em] text-tb-text/60">
              Stripe secured · 30-day refund if 0% progress
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ExamFormat() {
  return (
    <section className="py-20 border-b border-white/5" data-testid="gs-exam-format">
      <div className="max-w-6xl mx-auto px-6">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tb-teal uppercase mb-4">
          Section 03·B · Practice Exam Format
        </div>
        <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mb-3">
          Practice exams mirror the real thing.
        </h2>
        <p className="text-tb-text/70 max-w-2xl mb-10">
          Our full-length practice exams use the exact same format as the
          Transport Canada Small Advanced Exam — so on exam day, nothing catches
          you off guard.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SpecCard k="Questions" v="50" note="Sampled from a large bank" />
          <SpecCard k="Time limit" v="60 min" note="Same as real exam" />
          <SpecCard k="Pass mark" v="80%" note="Same as Transport Canada" />
          <SpecCard k="Cooldown" v="90 min" note="TC exam requires 24 h" />
        </div>
        <p className="mt-6 text-xs text-tb-text/55 font-mono tracking-wide max-w-3xl">
          <span className="text-tb-teal">Note ·</span> Our 90-minute practice
          cooldown is intentional — it lets you review missed rationale, revisit
          weak modules, and re-attempt while the material is still warm. The
          real Transport Canada Small Advanced Exam enforces a full <b>24-hour
          rewrite waiting period</b> between attempts. Same format, more lenient
          cadence for study.
        </p>
      </div>
    </section>
  );
}

function SpecCard({ k, v, note }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
      <div className="font-mono text-[10px] tracking-[0.2em] text-tb-text/60 uppercase">{k}</div>
      <div className="text-3xl font-display font-extrabold text-tb-teal mt-2 tracking-tight">{v}</div>
      <div className="mt-1 text-tb-text/60 text-xs font-mono">{note}</div>
    </div>
  );
}

function ComplianceDisclaimer() {
  return (
    <section className="py-16 border-b border-white/5" data-testid="gs-disclaimer">
      <div className="max-w-4xl mx-auto px-6">
        <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/[0.04] p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <AlertTriangle size={22} className="text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-mono text-[10px] tracking-[0.24em] text-yellow-300 uppercase mb-2">
                Important · Preparation material only
              </div>
              <h3 className="text-lg sm:text-xl font-display font-bold text-white mb-3 leading-snug">
                This course prepares you for the Transport Canada exam. It is not the exam.
              </h3>
              <div className="text-tb-text/80 text-sm leading-relaxed space-y-2.5">
                <p>
                  Thunderbird Geospatial does not administer the Transport Canada Small
                  Advanced Exam and does not issue the Advanced Operations RPAS Pilot
                  Certificate. To earn the certificate you must independently:
                </p>
                <ol className="list-decimal pl-5 space-y-1.5 marker:text-tb-teal marker:font-mono">
                  <li>
                    Register on the <strong>Transport Canada Drone Management Portal</strong> and
                    write the Small Advanced Exam (80% pass mark).
                  </li>
                  <li>
                    Book and pass a <strong>Flight Review</strong> with a Transport
                    Canada–approved Flight Reviewer.
                  </li>
                </ol>
                <p className="text-tb-text/60 text-xs font-mono pt-2">
                  Content is aligned with Transport Canada's TP 15263 (03/2025)
                  Knowledge Requirements for Pilots of Remotely Piloted Aircraft
                  Systems (CARs Part IX; AC 903-001).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- FAQ ---------------------------- */
function FAQSection() {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-20 border-b border-white/5" data-testid="gs-faq">
      <div className="max-w-4xl mx-auto px-6">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tb-teal uppercase mb-4">
          Section 05 · Common Questions
        </div>
        <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mb-10">
          What most students ask before enrolling.
        </h2>
        <div className="space-y-2">
          {FAQ.map((f, i) => {
            const isOpen = i === open;
            return (
              <div
                key={f.q}
                className={`rounded-lg border transition-all ${
                  isOpen
                    ? "border-tb-teal/40 bg-white/[0.04]"
                    : "border-white/10 bg-white/[0.015] hover:border-white/20"
                }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
                  data-testid={`gs-faq-toggle-${i}`}
                >
                  <span className="font-display font-semibold text-base">{f.q}</span>
                  <ChevronRight
                    size={16}
                    className={`text-tb-teal transition-transform ${isOpen ? "rotate-90" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-tb-text/75 text-sm leading-relaxed">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- WAITLIST ---------------------------- */
function Waitlist() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", experience: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/groundschool/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setDone(true);
      toast.success("You're on the list. We'll be in touch at launch.");
    } catch (ex) {
      toast.error(typeof ex.message === "string" ? ex.message : "Signup failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="waitlist" className="py-20" data-testid="gs-waitlist">
      <div className="max-w-3xl mx-auto px-6">
        <div className="font-mono text-[10px] tracking-[0.3em] text-tb-teal uppercase mb-4 text-center">
          Section 06 · Mailing List
        </div>
        <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-center mb-3">
          Stay in the loop.
        </h2>
        <p className="text-tb-text/70 text-center max-w-xl mx-auto mb-10">
          Join our mailing list for updates on new courses and changes to
          Transport Canada's regulations.
        </p>

        {done ? (
          <div
            className="mx-auto max-w-md rounded-xl border border-tb-teal/40 bg-tb-teal/5 p-8 text-center"
            data-testid="gs-waitlist-success"
          >
            <CheckCircle2 size={32} className="text-tb-teal mx-auto mb-3" />
            <div className="font-display font-bold text-lg mb-1">You're on the list.</div>
            <div className="text-tb-text/70 text-sm">
              We'll email <span className="text-tb-teal">{form.email}</span> when there's news.
            </div>
            <Link
              to="/"
              className="inline-block mt-6 font-mono text-[10px] tracking-[0.25em] text-tb-text/60 hover:text-tb-teal uppercase"
            >
              ← Back to the site
            </Link>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mx-auto max-w-xl rounded-xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 space-y-4"
            data-testid="gs-waitlist-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Full name"
                required
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                testid="gs-waitlist-name"
              />
              <Field
                label="Email"
                type="email"
                required
                value={form.email}
                onChange={(v) => setForm({ ...form, email: v })}
                testid="gs-waitlist-email"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Phone (optional)"
                type="tel"
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                testid="gs-waitlist-phone"
              />
              <Field
                label="Aviation experience (optional)"
                placeholder="Student pilot, RPP holder, curious…"
                value={form.experience}
                onChange={(v) => setForm({ ...form, experience: v })}
                testid="gs-waitlist-experience"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full mt-2 px-6 py-3 rounded-full bg-tb-teal text-black font-semibold hover:brightness-110 transition disabled:opacity-60"
              data-testid="gs-waitlist-submit"
            >
              {busy ? "Joining…" : "Join the list"}
            </button>
            <p className="text-[10px] font-mono tracking-[0.15em] text-tb-text/50 text-center pt-1">
              We'll only email you about course updates and regulatory changes, not anything else.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}

function Field({ label, required, type = "text", value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] tracking-[0.2em] text-tb-text/60 uppercase mb-1.5">
        {label}
        {required && <span className="text-tb-teal ml-1">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full bg-black/40 border border-white/10 focus:border-tb-teal outline-none px-3 py-2.5 rounded-md text-sm font-mono transition"
        data-testid={testid}
      />
    </label>
  );
}
